import apiClient from '@/api/client'
import { DEBUG_MODE } from '@/config/api'
import {
  type GraphQLAuthExtension,
  handleGraphQLAuthError,
  shouldAttemptRefresh,
} from '@/api/graphqlAuthRetry'

const GRAPHQL_ENDPOINT = '/graphql/'

/**
 * GraphQL Error structure
 */
export interface GraphQLError {
  message: string
  extensions?: Record<string, unknown>
}

/**
 * GraphQL Response structure with auth extensions
 */
export interface GraphQLResponse<T> {
  data?: T
  errors?: GraphQLError[]
  extensions?: {
    auth?: GraphQLAuthExtension
  }
}

/**
 * Execute a GraphQL query with automatic auth retry
 * 
 * @param query - GraphQL query string
 * @param variables - Optional query variables
 * @param isRetry - Internal flag to prevent infinite retry loops
 * @returns GraphQL response data
 */
export async function graphqlQuery<T>(
  query: string,
  variables?: Record<string, any>,
  isRetry = false
): Promise<GraphQLResponse<T>> {
  if (DEBUG_MODE) {
    console.log('GraphQL Query:', {
      endpoint: GRAPHQL_ENDPOINT,
      query: query.trim().substring(0, 100) + '...',
      variables,
      isRetry,
    })
  }
  
  try {
    const response = await apiClient.post<GraphQLResponse<T>>(
      GRAPHQL_ENDPOINT,
      {
        query,
        variables,
      }
    )

    const responseData = response.data

    // Check for authentication errors first (before checking GraphQL errors)
    const authExtension = responseData.extensions?.auth
    
    if (!isRetry && shouldAttemptRefresh(authExtension)) {
      console.log('[GraphQL] Token expired, attempting refresh...')
      
      const { shouldRetry, newToken } = await handleGraphQLAuthError(
        authExtension,
        responseData.errors,
        responseData.extensions
      )
      
      if (shouldRetry && newToken) {
        console.log('[GraphQL] Retrying query with new token...')
        // Retry the query with the new token
        return graphqlQuery<T>(query, variables, true)
      }
    }

    // Log response details
    if (DEBUG_MODE) {
      console.log('GraphQL Response:', {
        hasData: !!responseData.data,
        hasErrors: !!responseData.errors,
        errors: responseData.errors,
        authExtension: authExtension
          ? {
              isAuthenticated: authExtension.isAuthenticated,
              reason: authExtension.reason,
            }
          : undefined,
      })
    }

    // Check for GraphQL errors
    if (responseData.errors && responseData.errors.length > 0) {
      const errorMessage = responseData.errors.map(e => e.message).join('; ')
      
      // If we have partial data, log warning but don't throw
      if (responseData.data) {
        console.warn('[GraphQL] GraphQL returned errors but also data:', errorMessage)
        return responseData
      }
      
      // If no data and we have errors, throw
      throw new Error(errorMessage)
    }

    return responseData
  } catch (error: any) {
    // If it's an HTTP error (not GraphQL error), check if it's auth-related
    if (error.response?.status === 401 && !isRetry) {
      console.log('[GraphQL] HTTP 401 error, attempting token refresh...')
      
      const { shouldRetry, newToken } = await handleGraphQLAuthError(
        null,
        undefined,
        undefined
      )
      
      if (shouldRetry && newToken) {
        console.log('[GraphQL] Retrying query after token refresh...')
        return graphqlQuery<T>(query, variables, true)
      }
    }

    console.error('GraphQL Request Failed:', {
      endpoint: GRAPHQL_ENDPOINT,
      error: error.message,
      response: error.response?.data,
      status: error.response?.status,
    })
    
    throw error
  }
}
